import { computed, type ComputedRef, type Ref } from 'vue';

import { useSession } from '@/auth/session';
import type { Project } from '@/types/domain';

/**
 * 这个项目当前账号能不能改。
 *
 * 口径（Tony 2026-08-18 定）：**所有人都能看到所有项目，不是自己的项目只能读不能写**。
 * 所以要过两关——账号本身不是只读，且这个项目在自己名下（项目组成员、负责人，
 * 或者头上挂着这个项目的活）。后端 `writable` 字段和写入拦截用的是同一套判定，
 * 这里只决定按钮亮不亮。
 *
 * 两个字段都按「缺省可写」处理（`!== false` 而不是 `=== true`）：session 或项目详情
 * 还在路上时，按钮不该先灰一下再亮起来。真没权限的话后端会拦，
 * 而在自己的项目里把按钮全灰掉是实打实的倒退。
 */
export function useProjectWrite(
  project: Ref<Project | null | undefined> | ComputedRef<Project | null | undefined>,
): ComputedRef<boolean> {
  const { access } = useSession();
  return computed(() =>
    access.value?.canWrite !== false && project.value?.writable !== false);
}

/** 只读原因，给顶部横幅用；能写时返回空串 */
export function readOnlyReason(
  project: Project | null | undefined,
  accountCanWrite: boolean,
): string {
  if (accountCanWrite === false) return '当前账号是只读的，所有项目都只能查看。';
  if (project?.writable === false) {
    return '你不在这个项目组里，可以查看全部内容，但不能修改。'
      + '需要参与的话，让项目经理在「团队」里把你加进来。';
  }
  return '';
}

/**
 * 能不能改**这个项目的计划**（计划甘特 / 阶段工作两页）。
 *
 * 0914 Tony 第 12 条:「计划甘特和阶段工作这 2 个模块权限设置为
 * 只允许项目经理（管理者）有编辑权限」。
 *
 * 比 useProjectWrite 窄一档:排期是整条项目的骨架,一个人改一个日期,
 * 下游节点、Gate、里程碑、交付物到期日全跟着动。
 *
 * **这里用 `=== true` 而不是 `!== false`**,和 useProjectWrite 反过来:
 * 那边缺省可写是为了「自己的项目里按钮不要先灰一下再亮」;
 * 这边缺省不可写——字段还没到、或者后端是旧版本没这个字段时,
 * 宁可先灰着（点了也会被后端拦,亮着只会让人白填一遍）。
 */
export function useProjectPlanWrite(
  project: Ref<Project | null | undefined> | ComputedRef<Project | null | undefined>,
): ComputedRef<boolean> {
  const { access } = useSession();
  return computed(() =>
    access.value?.canWrite !== false
    && project.value?.writable !== false
    && project.value?.planWritable === true);
}

/** 计划只读的原因，给两页顶部的横幅用；能改时返回空串 */
export function planReadOnlyReason(
  project: Project | null | undefined,
  accountCanWrite: boolean,
): string {
  const general = readOnlyReason(project, accountCanWrite);
  if (general) return general;
  if (project?.planWritable !== true) {
    /* 这段话直接显示在界面上，别写 Markdown 的星号——它不会被渲染成粗体，只会当成星号 */
    return '排期只有本项目的项目经理能改 —— 日期一动，下游节点、Gate 和交付物的到期日都会跟着变。'
      + '要调日期、改责任人或改评审角色，把内容发给本项目的项目经理。'
      + '你自己那条活照常能报：在「阶段工作」里点那一行，右侧直接填完成度和实际完成；'
      + '开始 / 完成也可以在「我的工作」里点。';
  }
  return '';
}
